import { Loading } from "@components/Loading";
import { TopNav } from "@components/TopNav";
import { useTranslation } from "@helpers/useTranslation";
import { useEffect, useMemo, useState } from "react";
import { trackEvent } from "src/helpers/analytics";
import { useEventId } from "src/helpers/useEvent";
import { useSimpleCollection } from "src/helpers/useSimpleCollection";
import { Wheel, WheelEntry } from "./Wheel";
import { WinnerModal } from "./WinnerModal";

type Filter = "all" | "completed1" | "completed10" | "completedAll";

const completedCount = (team: Team): number => Object.keys(team.solutionsMap || {}).length;

// Song, oracle and info challenges never write a solutionsMap entry, so counting them would make
// the "completedAll" filter unreachable in events that have any of them.
const isCompletable = (challenge: Challenge): boolean => !["song", "oracle", "info"].includes(challenge.type);

const filterTeams = (teams: Team[], challengeCount: number, filter: Filter): Team[] => {
  if (filter === "all") return teams;
  if (filter === "completed1") return teams.filter((t) => completedCount(t) >= 1);
  if (filter === "completed10") return teams.filter((t) => completedCount(t) >= 10);
  if (filter === "completedAll") {
    if (challengeCount === 0) return [];
    return teams.filter((t) => completedCount(t) >= challengeCount);
  }
  return teams;
};

const labelFor = (team: Team): string => team.displayName ?? team.name ?? team.id;

// ONNINEN one-off — remove with onninen customisation
const onninenCompanyFor = (team: Team): string | undefined => {
  const company = (team as Team & { onninen?: { company?: string } }).onninen?.company?.trim();
  return company || undefined;
};

export const WheelOfFortune = () => {
  const { t } = useTranslation("admin");
  const eventId = useEventId();
  const [teams, teamsLoading] = useSimpleCollection<Team>(`events/${eventId}/teams`);
  const [challenges, chLoading] = useSimpleCollection<Challenge>(`events/${eventId}/challenges`);

  const [filter, setFilter] = useState<Filter>("completed10");
  const [removedIds, setRemovedIds] = useState<Set<string>>(new Set());
  const [spinTarget, setSpinTarget] = useState<number | null>(null);
  const [pendingWinner, setPendingWinner] = useState<WheelEntry | null>(null);
  const [winner, setWinner] = useState<WheelEntry | null>(null);

  useEffect(() => {
    trackEvent("wheel_of_fortune_view");
  }, []);

  const entries: WheelEntry[] = useMemo(() => {
    const eligible = filterTeams(teams || [], (challenges || []).filter(isCompletable).length, filter);
    return eligible
      .filter((team) => !removedIds.has(team.id))
      .map((team) => {
        const company = onninenCompanyFor(team);
        return {
          id: team.id,
          label: labelFor(team),
          ...(company ? { subtitle: company } : {}),
        };
      });
  }, [teams, challenges, filter, removedIds]);

  const isSpinning = spinTarget !== null;

  const handleSpin = () => {
    if (!entries.length || isSpinning) return;
    const idx = Math.floor(Math.random() * entries.length);
    setPendingWinner(entries[idx]);
    setWinner(null);
    setSpinTarget(idx);
    trackEvent("wheel_of_fortune_spin");
  };

  const handleSpinComplete = () => {
    if (pendingWinner) setWinner(pendingWinner);
  };

  const closeModal = () => {
    setWinner(null);
    setPendingWinner(null);
    setSpinTarget(null);
  };

  const handleKeep = () => closeModal();

  const handleRemove = () => {
    if (winner) {
      setRemovedIds((prev) => {
        const next = new Set(prev);
        next.add(winner.id);
        return next;
      });
    }
    closeModal();
  };

  const handleFilterChange = (newFilter: Filter) => {
    setFilter(newFilter);
    setRemovedIds(new Set());
  };

  const handleReset = () => {
    setRemovedIds(new Set());
    setSpinTarget(null);
    setPendingWinner(null);
    setWinner(null);
  };

  return (
    <>
      <TopNav isAdmin showBack backLink="/admin" />
      <main className="flex-1">
        <Loading loading={teamsLoading || chLoading}>
          <div className="mx-auto max-w-5xl px-6 py-8">
            <h1 className="mb-2 text-center text-3xl font-semibold">{t("wheelPageTitle")}</h1>
            <p className="mx-auto mb-8 max-w-xl text-center text-sm text-gray-500">{t("wheelOfFortuneDesc")}</p>

            <div className="grid grid-cols-1 gap-8 md:grid-cols-[320px,1fr] md:items-start">
              <div className="flex flex-col gap-4">
                <div>
                  <label className="label">
                    <span className="label-text font-semibold">{t("eligibility")}</span>
                  </label>
                  <select
                    className="select-bordered select w-full"
                    value={filter}
                    onChange={(e) => handleFilterChange(e.target.value as Filter)}
                    disabled={isSpinning}
                  >
                    <option value="all">{t("filterAllTeams")}</option>
                    <option value="completed1">{t("filterCompletedOne")}</option>
                    <option value="completed10">{t("filterCompletedTen")}</option>
                    <option value="completedAll">{t("filterCompletedAll")}</option>
                  </select>
                </div>

                <div>
                  <div className="mb-2 flex items-baseline justify-between">
                    <span className="label-text font-semibold">{t("entries")}</span>
                    <span className="text-xs text-gray-500">{t("numberOfEntries", { count: entries.length })}</span>
                  </div>
                  <ul className="max-h-72 overflow-y-auto rounded-lg border border-base-300 bg-base-100">
                    {entries.length === 0 ? (
                      <li className="p-3 text-sm text-gray-500">{t("noEligibleTeams")}</li>
                    ) : (
                      entries.map((entry) => (
                        <li key={entry.id} className="border-b border-base-200 px-3 py-2 text-sm last:border-b-0">
                          <div>{entry.label}</div>
                          {entry.subtitle && <div className="text-xs text-gray-500">{entry.subtitle}</div>}
                        </li>
                      ))
                    )}
                  </ul>
                  {removedIds.size > 0 && (
                    <button onClick={handleReset} disabled={isSpinning} className="btn-outline btn-sm btn mt-3 w-full">
                      {t("resetList", { count: removedIds.size })}
                    </button>
                  )}
                </div>
              </div>

              <div className="flex flex-col items-center">
                <Wheel entries={entries} spinTarget={spinTarget} onSpinComplete={handleSpinComplete} />
                <button
                  onClick={handleSpin}
                  disabled={!entries.length || isSpinning}
                  className="btn-primary btn mt-6 w-48"
                >
                  {isSpinning ? t("spinning") : t("spin")}
                </button>
              </div>
            </div>
          </div>
        </Loading>
      </main>
      {winner && <WinnerModal winner={winner} onKeep={handleKeep} onRemove={handleRemove} />}
    </>
  );
};
